import { ValueOf } from 'lib/core/types';

import TCustomerProfile from 'lib/common/contexts/ContactContext/types/CustomerProfile';
import taskBuilder from 'lib/common/contexts/ContactContext/utils/taskBuilder/index';

import CONNECTION_STATES from 'lib/common/constants/connectionStates';
import CONNECTION_TYPES from 'lib/common/constants/connectionType';
import CONTACT_STATES from 'lib/common/constants/contactStates';
import CONTACT_TYPE from 'lib/common/constants/contactTypes';

import Task from 'lib/common/types/Task';
import connectGetter from 'lib/common/utils/connectGetter';

import getTaskType from '../../getTaskType';
import { PREVIEW_DIALING_CONNECT_CONTACT_STATE_MAP } from '../constants';
import { getConnectionValueFromContact } from '../utils';

export default async function updatedPreviewDialingTask({
  existingTask,
  updatedContact,
  taskProps,
  status,
  connectionState,
  profile,
}: {
  existingTask: Task;
  updatedContact: connect.Contact;
  taskProps: Partial<Task>;
  status?: ValueOf<typeof CONTACT_STATES>;
  connectionState?: ValueOf<typeof CONNECTION_STATES>;
  profile?: TCustomerProfile;
}) {
  // A preview dialing task can transition into a call task, so we need
  // to check the task type based on the updated contact, not the existing task
  const type = getTaskType(updatedContact) || CONTACT_TYPE.PREVIEW_DIALING;

  const updatedTask = taskBuilder.updatedBaseTask({
    existingTask,
    updatedContact,
    taskProps,
    type,
    status,
    connectionState,
    profile,
  });

  // When the preview dialing task transitions into a
  // connecting call task, we set the connectionType to outbound
  const connectionType =
    status === CONTACT_STATES.CONNECTING && type === 'CALL' ? CONNECTION_TYPES.OUTBOUND : CONNECTION_TYPES.AGENT;

  const contactStateType = connectGetter(updatedContact, 'getState')?.type;

  // When we've disabled the Customer Profiles Permission on Admin,
  // we want to update the connectionValue to show the phone number
  // instead of the CAMPAIGN NAME Contact Attribute, if set
  const connectionValue =
    type === 'CALL' ? getConnectionValueFromContact(updatedContact) : existingTask.connectionValue;

  // newBaseTask converts Pending into Accepted and Error into Missed
  // for other task types
  // but for preview dialing tasks, we want to keep it as is
  const updatedStatus = (contactStateType && PREVIEW_DIALING_CONNECT_CONTACT_STATE_MAP[contactStateType]) || status;

  return { ...updatedTask, connectionValue, connectionType, ...(updatedStatus ? { status: updatedStatus } : {}) };
}
